import { memo, useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

// Components
import Column from './Column';
import Items from './Item';
import styles from './BaseBoard.module.scss';
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import BaseScrollbar from '../base-scrollbar/BaseScrollbar';

export type ColumnType = {
  uuid: UniqueIdentifier;
  id: number;
  name: string;
  items: ItemType[];
  color?: string;
  backgroundColor?: string;
};

export type ItemType = {
  uuid: UniqueIdentifier;
  id: number;
  name: string;
};

export type BaseBoardProps = {
  data: any;
  renderColumn?: (data: ColumnType) => React.ReactNode;
  renderItem?: (data: any, columData: any) => React.ReactNode;
  onItemChange?: (item: ItemChangeProps) => void;
  columnClassName?: string;
  disabled?: boolean;
};

export type ItemChangeProps = {
  columnId: string;
  columnPosition: string;
  itemId: string;
  itemPosition: string;
  data: any;
};

const cx = classNames.bind(styles);

function BaseBoard(props: BaseBoardProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [columns, setColumns] = useState<ColumnType[]>([]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.data) {
      setColumns(
        props.data.map((column: ColumnType) => {
          return {
            ...column,
            items: column.items.map((item: ItemType) => {
              return { ...item, itemId: item.uuid, uuid: `item-${item.id || 1}` };
            }),
            columnId: column.uuid,
            uuid: `column-${column.id || 1}`,
          };
        })
      );
    }
  }, [props.data]);
  //#endregion Implement Hook

  //#region Handle Function
  function findValueOfItems(uuid: UniqueIdentifier | undefined, type: string) {
    if (type === 'column') return columns.find((item) => item.uuid === uuid);
    if (type === 'item') return columns.find((column) => column.items.find((item) => item.uuid === uuid));
  }

  const findItem = (uuid: UniqueIdentifier | undefined) => {
    const column = findValueOfItems(uuid, 'item');
    if (!column) return null;
    const item = column.items.find((item) => item.uuid === uuid);
    if (!item) return null;

    return item;
  };
  //#endregion Handle Function

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const { id } = active;
    setActiveId(id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    // Handling item Sorting
    if (
      active.id.toString().includes('item') &&
      over?.id.toString().includes('item') &&
      active &&
      over &&
      active.id !== over.id
    ) {
      // Find the active and over column
      const activeContainer = findValueOfItems(active.id, 'item');
      const overContainer = findValueOfItems(over.id, 'item');

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = columns.findIndex((column) => column.uuid === activeContainer.uuid);
      const overColumnIndex = columns.findIndex((column) => column.uuid === overContainer.uuid);

      // Find the index of the active and over item
      const activeItemIndex = activeContainer.items.findIndex((item) => item.uuid === active.id);
      const overItemIndex = overContainer.items.findIndex((item) => item.uuid === over.id);
      // In the same column
      let newItems = [...columns];
      if (activeColumnIndex === overColumnIndex) {
        newItems[activeColumnIndex].items = arrayMove(
          newItems[activeColumnIndex].items,
          activeItemIndex,
          overItemIndex
        );

        setColumns(newItems);
      } else {
        // In different columns
        const [removeditem] = newItems[activeColumnIndex].items.splice(activeItemIndex, 1);
        newItems[overColumnIndex].items.splice(overItemIndex, 0, removeditem);
        setColumns(newItems);
      }

      if (props.onItemChange) {
        props.onItemChange({
          columnId: String(overContainer?.id),
          columnPosition: String(overColumnIndex + 1),
          itemId: String(activeId).replace('item-', ''),
          itemPosition: String(overItemIndex + 1),
          data: newItems,
        });
      }
    }

    // Handling item dropping into Column
    if (
      active.id.toString().includes('item') &&
      over?.id.toString().includes('column') &&
      active &&
      over &&
      active.id !== over.id
    ) {
      // Find the active and over column
      const activeContainer = findValueOfItems(active.id, 'item');
      const overContainer = findValueOfItems(over.id, 'column');

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = columns.findIndex((column) => column.uuid === activeContainer.uuid);
      const overColumnIndex = columns.findIndex((column) => column.uuid === overContainer.uuid);

      // Find the index of the active and over item
      const activeItemIndex = activeContainer.items.findIndex((item) => item.uuid === active.id);

      let newItems = [...columns];
      const [removeditem] = newItems[activeColumnIndex].items.splice(activeItemIndex, 1);
      newItems[overColumnIndex].items.push(removeditem);
      setColumns(newItems);

      if (props.onItemChange) {
        props.onItemChange({
          columnId: String(overContainer?.id),
          columnPosition: String(overColumnIndex + 1),
          itemId: String(activeId).replace('item-', ''),
          itemPosition: String(overColumnIndex + 1),
          data: newItems,
        });
      }
    }

    setActiveId(null);
  }

  return (
    <Box className='flex gap-6'>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <BaseScrollbar>
          <SortableContext items={columns.map((i) => i.uuid)}>
            <Box className='flex gap-4'>
              {columns.map((column) => (
                <Column id={column.uuid} key={column.uuid} render={props.renderColumn} data={column}>
                  <SortableContext items={column.items.map((i) => i.uuid)}>
                    <Box
                      className={cx(
                        'flex items-start flex-col gap-y-4 overflow-auto h-[500px]',
                        'scroll-hidden',
                        props.columnClassName
                      )}
                    >
                      {column.items.map((item) => (
                        <Items
                          data={item}
                          columnData={column}
                          id={item.uuid}
                          key={item.uuid}
                          render={props.renderItem}
                          disabled={props.disabled}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                </Column>
              ))}
            </Box>
          </SortableContext>
        </BaseScrollbar>
        <DragOverlay adjustScale={false}>
          {activeId && activeId.toString().includes('item') && (
            <Items id={activeId} data={findItem(activeId)} render={props.renderItem} />
          )}
        </DragOverlay>
      </DndContext>
    </Box>
  );
}

export default memo(BaseBoard);
